import type { TabsProps } from "antd";
import { create, type StateCreator } from "zustand";
import { persist, createJSONStorage, devtools } from "zustand/middleware";

type Tab = NonNullable<TabsProps["items"]>[number];
interface MenusStore {
  activeKey: string;
  lastActivePath: string;
  originalRoute: string;
  tabs: Tab[];
  onTabClose?: (val: string) => void;
  addTab: (tab: Tab) => void;
  removeTab: (key: string) => void;
  setOnTabClose: (onTabClose: (val: string) => void) => void;
  clearTabs: () => void;
  setOriginalRoute(route: string): void;
  setActiveKey: (key?: string) => void;
  setLastActivePath: (path?: string) => void;
  updateActiveTab(key: string, tab: Partial<Tab>): void;
}

const initialMenusState = {
  tabs: [] as Tab[],
  originalRoute: "",
  activeKey: "",
  lastActivePath: "",
} satisfies Partial<MenusStore>;
const menuStoreCreator: StateCreator<MenusStore> = (set, get) => ({
  ...initialMenusState,
  addTab: (tab) => {
    const tabs = get().tabs;
    if (tabs.some((t) => t.key === tab.key)) {
      set({ activeKey: tab.key });
    } else {
      set(() => ({ tabs: [...get().tabs, tab], activeKey: tab.key }));
    }
  },
  removeTab(key) {
    const tabs = [...get().tabs];
    const index = tabs.findIndex((t) => t.key === key);
    tabs.splice(index, 1);
    let activeKey: string;
    if (tabs[index]?.key) {
      activeKey = tabs[index].key;
    } else {
      activeKey = tabs[index - 1].key;
    }
    set({ tabs, activeKey });
    get().onTabClose?.(activeKey);
  },
  setOnTabClose: (onTabClose) => set({ onTabClose }),
  clearTabs: () => {
    get().onTabClose?.(get().activeKey);
    set(initialMenusState);
  },
  updateActiveTab(key: string, tab: Partial<Tab>) {
    const tabs = get().tabs;
    const activeKey = get().activeKey;
    const index = tabs.findIndex((t) => t.key === activeKey);
    tabs[index] = { ...tabs[index], ...tab };
    set({ tabs, activeKey: tab.key });
  },
  setOriginalRoute: (route: string) => set({ originalRoute: route }),
  setActiveKey(key) {
    set({ activeKey: key });
  },
  setLastActivePath(path) {
    set({ lastActivePath: path });
  },
});
export const useMenusStore = create<MenusStore>()(
  devtools(
    persist(menuStoreCreator, {
      name: "menu-storage",
      storage: createJSONStorage(() => sessionStorage),
    })
  )
);
